
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { DatePicker } from "@/components/ui/datepicker";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

interface DateDisplayProps {
  date: Date;
  readOnly: boolean;
  onDateEdit?: (newDate: Date) => void;
}

const DateDisplay = ({ date, readOnly, onDateEdit }: DateDisplayProps) => {
  const { language } = useLanguage();
  const [isEditingDate, setIsEditingDate] = useState(false);
  
  const handleDateEdit = (selectedDate: Date | undefined) => {
    if (selectedDate && onDateEdit) {
      onDateEdit(selectedDate);
      setIsEditingDate(false);
    }
  };
  
  const formatDate = (date: Date) => {
    if (language === 'ja') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${
        ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'][date.getDay()]
      }`;
    } else if (language === 'es') {
      const weekdays = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
      const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
      return `${weekdays[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
    } else if (language === 'zh') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${
        ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][date.getDay()]
      }`;
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  return (
    <>
      {isEditingDate ? (
        <DatePicker 
          date={date} 
          onDateChange={handleDateEdit} 
          className="w-52" 
        />
      ) : (
        <div className="flex items-center gap-1">
          <span className="text-sm text-muted-foreground">
            {formatDate(date)}
          </span>
          {!readOnly && (
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsEditingDate(true)}>
              <CalendarIcon className="h-3.5 w-3.5" />
              <span className="sr-only">
                {t('common.editDate', language)}
              </span>
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default DateDisplay;
